/**
 * Calendar Calculations Utility
 * 
 * Purpose:
 * Pure functions for calendar date calculations, including month boundary calculations,
 * occupied nights calculations for reservations within a given month, and year extraction
 * from calendar data for dropdown options.
 * 
 * High-level Workflow:
 * 1. getMonthBounds: Takes a Date object representing any day in a month and calculates
 *    the first day (00:00:00) and last day (23:59:59.999) of that month.
 * 2. calculateOccupiedNightsInMonth: Calculates how many nights a reservation occupies
 *    within a specific month, accounting for reservations that span month boundaries.
 *    The checkout day is NOT counted as occupied (checkout day is available for next guest).
 * 3. getYearsFromCalendarData: Extracts unique years from bookings (check_in/check_out)
 *    and availability days, returns them sorted descending with current year first.
 * 
 * Assumptions & Invariants:
 * - All dates are JavaScript Date objects in local timezone
 * - Month boundaries are calculated in local time (not UTC)
 * - Checkout day is NOT occupied (reservation ends at checkout, so that day is available)
 * - Occupied nights are calculated as [checkin, checkout) - half-open interval
 * - If checkout is before or equal to checkin, returns 0 nights
 * - Month boundaries are clipped: if checkin is before month start, use month start;
 *   if checkout is after month end, use month end
 * 
 * Dependencies:
 * - Internal: parseLocalDate (YYYY-MM-DD parsing, no timezone shift)
 * - External: None
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 1 refactoring)
 * - 2026-01-28: Added getYearsFromCalendarData for dynamic year dropdown (calendar year dropdown fix)
 */

/**
 * Calculate the start and end dates of a month
 * 
 * This function takes any date within a month and returns the first moment
 * of the first day and the last moment of the last day of that month.
 * 
 * @param {Date} month - Date representing any day in the month
 * @returns {Object} { monthStart: Date, monthEnd: Date }
 *   - monthStart: First day of month at 00:00:00.000
 *   - monthEnd: Last day of month at 23:59:59.999
 */
export function getMonthBounds(month) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0); // Last day of month
  // Set to end of day for monthEnd
  monthEnd.setHours(23, 59, 59, 999);
  return { monthStart, monthEnd };
}

/**
 * Calculate how many nights a reservation occupies within a specific month
 * 
 * This function handles reservations that span month boundaries. For example,
 * if a reservation runs from January 28 to February 5, and we're calculating
 * for January, it will count the nights from January 28 to January 31 (4 nights).
 * 
 * Important: The checkout day is NOT counted as occupied. If someone checks out
 * on February 5, that day is available for the next guest, so we only count
 * nights up to (but not including) the checkout day.
 * 
 * @param {Date} checkIn - Check-in date (when guest arrives)
 * @param {Date} checkOut - Check-out date (when guest leaves - this day is NOT occupied)
 * @param {Date} monthStart - Start of the month we're calculating for
 * @param {Date} monthEnd - End of the month we're calculating for
 * @returns {number} Number of occupied nights within the month (0 or positive integer)
 */
export function calculateOccupiedNightsInMonth(checkIn, checkOut, monthStart, monthEnd) {
  // Clip to month boundaries
  const displayStart = checkIn > monthStart ? checkIn : monthStart;
  const displayEnd = checkOut < monthEnd ? checkOut : monthEnd;
  
  // Checkout day is NOT occupied, so we use [displayStart, displayEnd)
  // Count days from displayStart to displayEnd (exclusive)
  const startDay = new Date(displayStart);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(displayEnd);
  endDay.setHours(0, 0, 0, 0);
  
  if (endDay <= startDay) return 0;
  
  const diffTime = endDay - startDay;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Parse YYYY-MM-DD string to year (local timezone-safe).
 * Uses parseLocalDate when available; otherwise regex-only extraction to avoid deps.
 * @param {string} dateString
 * @returns {number|null}
 */
function parseYearFromDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return null;
  const m = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  return Number.isInteger(y) ? y : null;
}

/**
 * Extract unique years from calendar data (bookings + availability) for year dropdown.
 * Current year is always first; remaining years sorted descending. No unrelated years.
 *
 * @param {Array<{ check_in_date?: string; check_out_date?: string }>} bookings - Bookings used to render calendar (same as pills)
 * @param {Record<string, unknown>} availability - Availability days map (keys are YYYY-MM-DD)
 * @returns {number[]} Years for dropdown: [currentYear, ...others descending]
 */
export function getYearsFromCalendarData(bookings, availability) {
  const years = new Set();
  const currentYear = new Date().getFullYear();

  for (const b of bookings || []) {
    const y1 = parseYearFromDateString(b.check_in_date);
    if (y1 != null) years.add(y1);
    const y2 = parseYearFromDateString(b.check_out_date);
    if (y2 != null) years.add(y2);
  }

  const days = availability && typeof availability === 'object' ? availability : {};
  for (const key of Object.keys(days)) {
    const y = parseYearFromDateString(key);
    if (y != null) years.add(y);
  }

  const yearsFromData = [...years].sort((a, b) => b - a);
  const dropdown = [currentYear, ...yearsFromData.filter((y) => y !== currentYear)];
  return dropdown;
}
